// Commercial authorization of a campaign (ADR-0047), on the campaign page. Shown only when the
// organization works with purchase orders. Sending the request stops the campaign until Media
// Interactiva approves it; commercial approval and a published creative are BOTH needed to serve.
import { useQueryClient } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { ApiProblem } from "@/api/errors";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";
import { Callout, Panel, Section } from "@/ui/layout";
import { AUTH_STATUS, ruleMessage, usd, formField } from "./labels";
import { commercialKeys, useAuthorizations, useCompany, usePurchaseOrders } from "./queries";

const select =
  "h-8 w-full rounded-md border border-line-strong bg-surface px-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-brand";

export function CampaignAuthorization({ orgId, campaignId }: { orgId: string; campaignId: string }) {
  const { data: company } = useCompany(orgId);
  const enabled = company?.canManage === true && company.terms.purchaseOrdersEnabled;
  const { data: auths } = useAuthorizations(orgId, campaignId, enabled);
  const { data: orders } = usePurchaseOrders(orgId, enabled);
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const [key, setKey] = useState(() => crypto.randomUUID());
  if (!enabled) return null;
  const live = auths?.items.find((a) => a.status === "SUBMITTED" || a.status === "APPROVED");
  const latest = auths?.items[0];
  const approvedOrders = orders?.items.filter((o) => o.status === "APPROVED") ?? [];
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setPending(true);
    setError(undefined);
    try {
      await call(() =>
        api.POST("/orgs/{orgId}/campaign-authorizations", {
          params: { path: { orgId } },
          body: {
            campaignId,
            purchaseOrderId: formField(f, "purchaseOrderId"),
            budgetUsd: formField(f, "budgetUsd"),
            startsOn: formField(f, "startsOn"),
            endsOn: formField(f, "endsOn"),
            responsibleName: formField(f, "responsibleName"),
            idempotencyKey: key,
          },
        })
      );
      await queryClient.invalidateQueries({ queryKey: commercialKeys.authorizations(orgId, campaignId) });
      toast.success("Enviamos la solicitud. La campaña se distribuirá cuando Media Interactiva la autorice.");
      setFormOpen(false);
      setKey(crypto.randomUUID());
    } catch (problem) {
      setError(problem instanceof ApiProblem ? ruleMessage(problem) : "No pudimos enviar la solicitud.");
    } finally {
      setPending(false);
    }
  };
  return (
    <Section
      title="Autorización comercial"
      description="Las campañas financiadas con una orden de compra necesitan la autorización de Media Interactiva para distribuirse."
      actions={
        latest ? (
          <Badge tone={AUTH_STATUS[latest.status]?.tone ?? "neutral"}>{AUTH_STATUS[latest.status]?.label}</Badge>
        ) : null
      }
    >
      {latest ? (
        <Panel className="grid gap-1 p-3 text-sm">
          <div>
            Presupuesto solicitado: <strong>{usd(latest.requestedBudgetMicros)}</strong>
            {latest.approvedBudgetMicros ? (
              <>
                {" "}
                · aprobado: <strong>{usd(latest.approvedBudgetMicros)}</strong>
              </>
            ) : null}
          </div>
          <div className="text-xs text-muted">
            {latest.startsOn} → {latest.endsOn} · Responsable: {latest.responsibleName}
          </div>
          {latest.status === "REJECTED" && latest.decisionReason ? (
            <Callout title="Motivo del rechazo" tone="warning">
              {latest.decisionReason}
            </Callout>
          ) : null}
        </Panel>
      ) : null}
      {!live && !formOpen ? (
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              setFormOpen(true);
            }}
            disabled={approvedOrders.length === 0}
          >
            {latest?.status === "REJECTED" ? "Corregir y reenviar" : "Solicitar autorización"}
          </Button>
          {approvedOrders.length === 0 ? (
            <p className="mt-1 text-xs text-muted">Necesitas una orden de compra aprobada.</p>
          ) : null}
        </div>
      ) : null}
      {formOpen ? (
        <form className="grid max-w-xl gap-3" onSubmit={(e) => void submit(e)}>
          <Field label="Orden de compra">
            <select name="purchaseOrderId" className={select} required>
              {approvedOrders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.internalRef} · {o.poNumber} · {o.startsOn} → {o.endsOn}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Presupuesto (USD)">
              <Input name="budgetUsd" inputMode="decimal" pattern="\d{1,12}(\.\d{1,2})?" required />
            </Field>
            <Field label="Inicio">
              <Input name="startsOn" type="date" required />
            </Field>
            <Field label="Fin">
              <Input name="endsOn" type="date" required />
            </Field>
          </div>
          <Field label="Responsable">
            <Input name="responsibleName" maxLength={120} required />
          </Field>
          {error ? (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={pending}>
              {pending ? "Enviando…" : "Enviar a revisión"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setFormOpen(false);
              }}
            >
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
    </Section>
  );
}
